import React from 'react';
import {
  CertIDType,
  MemberType,
  OccType,
  RoomCleanStatus,
  RoomMainStatus,
  SourceChannelType,
  SEX_VALUE_SYMBOL,
  MemberLevel,
  CustomUnableBookStatus,
  EnableBookEnum,
} from '@/config/constant';
import { flatPriceList } from '@/redux/actions/reservation/helper';
import { DateType } from '@/utils/date';

export interface NoticeInfo {
  type: 'success' | 'info' | 'warning' | 'error';
  message?: React.ReactNode;
}

export interface CommonBase {
  memberLevel: MemberLevel;
  occType: OccType;
}

// 入住时间
export interface RentTimeInfo {
  readonly occType: OccType; // 入住类型
  businessDate?: DateType; // 所在营业日
  expectedArrivalTime?: DateType; // 预抵时间
  rentTime?: number; // 入住时长
  expectedDepartureTime?: DateType; // 预离时间
  readonly lastExtensionOccType: OccType; // 拓展字段里面的入住类型 （occType=多种 时才会赋值）
}

// 客源渠道
export interface SourceChannelInfo {
  channelId?: number; // 客源渠道ID
  strategyId?: number; // 计价策略ID
  channelOrderNo?: string; // 渠道单号
  otaPaymentAmount?: string; // OTA预付款
  invoiceAmount?: string; // 开票金额
}

// 客源渠道完整信息
export interface SourceChannelFullInfo extends SourceChannelInfo {
  channelName?: string; // 渠道名称
  strategyName?: string; // 策略名称
  otaOrderNo?: string; // OTA虚拟号
}

// 策略项
export interface StrategyItem {
  readonly id: number; // 策略id
  readonly name: string; // 策略名称
  readonly showName: string; // 策略展示名称
  readonly departureDate: string; // 策略预设离店时间
}

// 渠道项
export interface ChannelItem {
  readonly id: number; // 渠道id
  readonly name: string; // 渠道名称
  readonly type: SourceChannelType; // 渠道类型
  readonly directFlag: boolean; // 是否直连渠道
  readonly otaBillType: string; // 渠道代码
  readonly viewTag: string; // 渠道展示名称
  readonly priceStrategies?: StrategyItem[]; // 渠道策略(不能直接使用)
}

// 策略房间
export interface ConfigRoomPrice {
  readonly roomTypeId: number; // 房型id
  readonly deposit: number; // 押金
  readonly priceList: {
    readonly date: string; // 日期
    readonly realPrice: number; // 最终房价
  }[];
}

export interface BookingInfo {
  readonly enableBook: EnableBookEnum; // 是否可以预订：0-不可以，1-可以
  readonly occupyExpectArrivalTime: any; // 不可预订的占用订单
  readonly occupyExpectDepartureTime: any; // 不可预订的占用订单
  readonly occupyRecNo: string; // 不可预订的占用订单
  readonly occupyType: 1 | 2; // 占用类型：1-预订子单、2-待排订单
  readonly unableBookReasonType: 1 | 2 | 3; // 不可预订原因类型 1-房间维修，2-前有预订，3-后有预订
}

// 房间
export interface RoomItem {
  readonly id: number; // 房间id
  readonly roomNo: string; // 房间号码
  readonly roomTypeId: number; // 房型id
  readonly cleanStatus: RoomCleanStatus; // 房间脏净状态
  readonly mainStatus: RoomMainStatus; // 房间主房态
  readonly bookingInfo?: BookingInfo; // 房间预订信息
  readonly unableBookStatus?: CustomUnableBookStatus; // 前端自定义字段：不可预订状态：1-停，2-满
}

// 房型
export interface RoomCategory {
  readonly id: number; // 房型id
  readonly name: string; // 房型名称
  readonly roomsTotal: number; // 房型下房间总数
  readonly roomList: RoomItem[]; // 房型下房间列表
}

// 优惠券
export interface CouponValue {
  readonly couponValue: string; // 金额 元
  readonly couponAmount: string; // 金额 分
  readonly couponName: string; // 名称
  readonly couponNo: string; // 编码
  readonly couponType: string; // 类型
  readonly couponWyuid: string; // 所属用户id
  readonly couponSubType: string; // 券 子类型
  readonly crsCouponAmount: string; // 金额 分
}

// 入住日房价
export interface RoomPriceInfo extends CouponValue {
  readonly rentDateId: string; // 日期索引id
  readonly rentDate: string; // 入住日期(营业日)
  readonly occType: OccType; // 入住类型
  inputPrice: string; // 输入的房价
  isBeforeAudit?: boolean; // 时间是否已过夜审
  marketStrategyPrice?: number; // 后端用的字段
}

// 选中的房间
export interface SelectRoomItem {
  readonly indexId: string; // 索引id
  roomTypeId: number; // 房型id
  roomId?: number; // 房间id
}

// 入住房间
export interface RentRoomInfo {
  readonly indexId: string; // 索引id
  roomTypeId: number; // 房型id
  roomTypeName: string; // 房型名称
  roomId: number; // 房间id
  roomNo: string; // 房间号
  deposit?: string; // 押金
  guestList?: string[]; // 房间宾客id
  roomPriceList: RoomPriceInfo[]; // 入住日房价列表
}

// 备注信息
export interface RemarkInfo {
  remark: string; // 预订备注
  checkInRemark: string; // 入住备注
}

// 宾客基础信息
interface GuestBaseInfo {
  readonly indexId: string; // 索引id
  name: string; // 宾客名称
  certType: CertIDType; // 证件类型
  certNo: string; // 证件号码
  mobile: string; // 手机号
  email: string;
  isMember: boolean; // 是否会员卡会员
  memberLevel: MemberLevel;
  remark?: string; // 备注信息
  memberType: MemberType; // 宾客身份
}

// 已入住房间宾客信息
export interface RoomGuestItem extends GuestBaseInfo {
  roomNo: string; // 房间号
  remainingBalance: string; // 储值余额
}

// 新入住宾客信息
export interface CheckInGuestInfo extends GuestBaseInfo {
  wyuId: string; // 网鱼id
  balance: string; // 储值余额
  accountType?: number; // 账户类型
  source: 'READ' | 'INPUT'; // 信息来源 读取、输入
  sex: SEX_VALUE_SYMBOL; // 性别
  nation: string; // 民族
  birthday: number; // 出生
  certificateLocation: string; // 住址
  issuingAuthority: string; // 签发机关
  certificateStartTime: number; // 有效期限开始时间 时间戳
  certificateEndTime: number; // 有效期限结束时间
}

// 房价编辑信息
interface EditRoomPriceInfo extends RoomPriceInfo {
  isBeforeAudit: boolean; // 入住时间是否已过夜审
}

// 换房房价信息
export interface ExchangeRoomPriceInfo extends EditRoomPriceInfo {
  extensionId: number; // 拓展id
  priceChannel: SourceChannelType; // 价格渠道(判断是否可用券)
}

// 换房 房间类型
export enum ExchangeRoomRole {
  OriginRoom, // 原房间
  TargetRoom, // 新房间
}

// 换房入住房间信息
export interface ExchangeRentRoomInfo extends RentRoomInfo {
  itemRole: ExchangeRoomRole; // 房间类型 原房间、新房间
  roomPriceList: ExchangeRoomPriceInfo[]; // 入住日房价列表
  roomCardCount?: number; // 房卡数量
}

// 换房类型
export enum RoomExchangeType {
  KeepSame, // 同价换房
  Modify, // 改价换房
}

// 扁平的房价数据
export type FlatPriceMap = ReturnType<typeof flatPriceList>;

// 编辑入住房间信息
export interface EditRentRoomInfo extends RentRoomInfo {
  roomPriceList: EditRoomPriceInfo[]; // 房价列表
  recNo: string; // 子单号
}
